import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowDownLeftIcon,
  CheckIcon,
  CoinIcon,
  CopyIcon,
  QrCodeIcon,
  RocketLaunchIcon,
  SpinnerIcon,
  WarningIcon,
} from '@phosphor-icons/react/dist/ssr';

import { AuthLoadingScreen } from '@/components/templates';
import { ScreenShell } from '@/components/screen-shell';
import { NavBar } from '@/components/nav-bar';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardEyebrow,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { KeyValue } from '@/components/ui/key-value';
import { cn } from '@/lib/utils';

import {
  createOrderDraft,
  deployWallet,
  getBridgeAddress,
  getWalletBalance,
  type BridgeAddressResponse,
  type WalletBalanceResponse,
} from '../api';
import { useAuth } from '../auth';
import { useT } from '../i18n';
import { useTelegramBackButton } from '../lib/telegram-back-button';
import { useNavStackEntry } from '../lib/nav-stack';
import { tryGetTelegram } from '../telegram';

type State =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | {
      kind: 'ready';
      data: WalletBalanceResponse;
      bridge: BridgeAddressResponse | null;
    };

const POLL_MS = 15_000;
const POLL_MS_INTENT = 5_000;

/**
 * Quando o bot redireciona pra cá com `?intent=buy&slug=...&outcome=...&usd=...&required=...`,
 * a gente mostra um banner contextual no topo + barra de progresso até o saldo bater
 * em `required`, e poll mais rápido pra UX percebida.
 */
type BuyIntent = {
  slug: string;
  outcome: string;
  usd: number;
  required: number;
};

export default function Deposit() {
  const auth = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [deploying, setDeploying] = useState(false);
  const [copied, setCopied] = useState(false);

  useTelegramBackButton();
  useNavStackEntry({ label: t('deposit.breadcrumb') });

  const intent = useMemo<BuyIntent | null>(() => {
    if (searchParams.get('intent') !== 'buy') return null;
    const slug = searchParams.get('slug') ?? '';
    const outcome = searchParams.get('outcome') ?? '';
    const usd = Number(searchParams.get('usd') ?? '');
    const required = Number(searchParams.get('required') ?? usd);
    if (!slug || !outcome || !Number.isFinite(usd) || usd <= 0) return null;
    return { slug, outcome, usd, required: Number.isFinite(required) ? required : usd };
  }, [searchParams]);

  const externalUserId =
    auth.state === 'ready' ? auth.auth.externalUserId : null;

  const refresh = useCallback(async () => {
    if (!externalUserId) return;
    try {
      const [data, bridge] = await Promise.all([
        getWalletBalance(externalUserId),
        getBridgeAddress(externalUserId).catch(() => null),
      ]);
      setState({ kind: 'ready', data, bridge });
    } catch (err) {
      setState({ kind: 'error', message: (err as Error).message });
    }
  }, [externalUserId]);

  useEffect(() => {
    if (!externalUserId) return;
    void refresh();
    const id = window.setInterval(refresh, intent ? POLL_MS_INTENT : POLL_MS);
    return () => window.clearInterval(id);
  }, [externalUserId, refresh, intent]);

  async function onDeploy() {
    if (!externalUserId) return;
    setDeploying(true);
    try {
      await deployWallet(externalUserId);
      await refresh();
    } catch (err) {
      setState({ kind: 'error', message: (err as Error).message });
    } finally {
      setDeploying(false);
    }
  }

  async function onCopy(address: string) {
    try {
      await navigator.clipboard.writeText(address);
      tryGetTelegram()?.HapticFeedback?.notificationOccurred?.('success');
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  }

  if (auth.state === 'loading' || state.kind === 'loading') {
    return (
      <AuthLoadingScreen
        title={t('deposit.loading_title')}
        description={t('deposit.loading_desc')}
        shortDescription={t('deposit.loading_short')}
      />
    );
  }
  if (auth.state === 'error') {
    return (
      <ScreenShell eyebrow={t('deposit.title')} title={t('deposit.title')}>
        <Alert tone="danger">
          <AlertTitle>{t('deposit.auth_failed')}</AlertTitle>
          <AlertDescription>{auth.error}</AlertDescription>
        </Alert>
      </ScreenShell>
    );
  }
  if (state.kind === 'error') {
    return (
      <ScreenShell eyebrow={t('deposit.title')} title={t('deposit.title')}>
        <Alert tone="danger">
          <AlertTitle>{t('deposit.load_failed')}</AlertTitle>
          <AlertDescription>{state.message}</AlertDescription>
        </Alert>
      </ScreenShell>
    );
  }

  const { address, pUsdBalance, isDeployed } = state.data;
  const bridgeAddr = state.bridge?.evm ?? null;
  const depositTarget = bridgeAddr ?? address;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=320x320&data=${encodeURIComponent(depositTarget)}`;

  return (
    <ScreenShell
      eyebrow={
        <span className="inline-flex items-center gap-1.5">
          <ArrowDownLeftIcon weight="fill" className="size-3" />
          {t('deposit.title')}
        </span>
      }
      title={t('deposit.title')}
      description={
        <span
          dangerouslySetInnerHTML={{
            __html: bridgeAddr
              ? t('deposit.blurb_bridge')
              : t('deposit.blurb_direct'),
          }}
        />
      }
    >
      <NavBar />
      {intent && (
        <IntentBanner
          intent={intent}
          balanceUsd={Number(pUsdBalance)}
          t={t}
          navigate={navigate}
          onBackToChat={() => tryGetTelegram()?.close?.()}
        />
      )}

      <Card className="items-stretch">
        <CardHeader>
          <CardEyebrow>
            <QrCodeIcon weight="fill" className="size-3" />
            {bridgeAddr ? t('deposit.bridge_label') : t('deposit.direct_label')}
          </CardEyebrow>
          <CardTitle className="text-base sm:text-lg">
            {t('deposit.qr_card_title')}
          </CardTitle>
          <CardDescription>
            {t('deposit.qr_card_desc')}
          </CardDescription>
        </CardHeader>

        <div className="relative mx-auto flex items-center justify-center">
          <span
            aria-hidden
            className="from-primary/40 absolute -inset-3 rounded-3xl bg-linear-to-br via-violet-500/20 to-sky-500/30 opacity-60 blur-xl"
          />
          <div className="relative overflow-hidden rounded-3xl bg-white p-3 ring-1 ring-foreground/10">
            <img
              src={qrSrc}
              alt={t('deposit.qr_alt')}
              width={232}
              height={232}
              className="block size-[208px] rounded-2xl bg-white sm:size-[232px]"
            />
          </div>
        </div>

        <div className="flex flex-col gap-2 rounded-2xl bg-foreground/10 p-3 ring-1 ring-foreground/15">
          <span className="text-muted-foreground text-[11px] font-semibold tracking-[0.14em] uppercase">
            {bridgeAddr ? t('deposit.bridge_label') : t('deposit.direct_label')}
          </span>
          <code className="text-foreground block break-all font-mono text-[12px] leading-relaxed sm:text-[13px]">
            {depositTarget}
          </code>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onCopy(depositTarget)}
            className={cn(
              'self-start',
              copied && 'ring-emerald-500/40 text-emerald-300',
            )}
          >
            {copied ? (
              <CheckIcon weight="bold" className="size-4" />
            ) : (
              <CopyIcon weight="bold" className="size-4" />
            )}
            {copied ? t('common.copied') : t('common.copy_address')}
          </Button>
        </div>

        <CardContent>
          <KeyValue
            label={t('common.balance')}
            value={
              <span className="text-foreground text-lg font-semibold tabular-nums">
                ${Number(pUsdBalance).toFixed(2)}
              </span>
            }
          />
          <KeyValue label={t('deposit.field_network')} value="Polygon" />
          <KeyValue
            label={t('deposit.field_token')}
            value={bridgeAddr ? 'USDC / USDC.e' : 'pUSD'}
          />
          <KeyValue
            label={t('deposit.field_status')}
            value={
              isDeployed ? (
                <Badge tone="success">{t('deposit.status_deployed')}</Badge>
              ) : (
                <Badge tone="warning">{t('deposit.status_not_deployed')}</Badge>
              )
            }
          />
        </CardContent>
      </Card>

      {!isDeployed && (
        <Card>
          <CardEyebrow className="text-amber-300">
            <WarningIcon weight="fill" className="size-3" />
            {t('deposit.action_required')}
          </CardEyebrow>
          <CardTitle className="text-base sm:text-lg">
            {t('deposit.not_deployed_title')}
          </CardTitle>
          <p className="text-muted-foreground text-[13px] leading-relaxed">
            {t('deposit.not_deployed_body')}
          </p>
          <Button size="lg" onClick={onDeploy} disabled={deploying}>
            {deploying ? (
              <SpinnerIcon weight="bold" className="size-4 animate-spin" />
            ) : (
              <RocketLaunchIcon weight="bold" className="size-4" />
            )}
            {deploying ? t('deposit.deploying') : t('deposit.deploy_btn')}
          </Button>
        </Card>
      )}

      <p className="text-muted-foreground/80 text-[12px] leading-relaxed">
        <CoinIcon weight="fill" className="-mt-0.5 mr-1 inline size-3.5 text-primary" />
        {bridgeAddr ? t('deposit.footer_bridge') : t('deposit.footer_direct')}
      </p>
    </ScreenShell>
  );
}

function IntentBanner({
  intent,
  balanceUsd,
  t,
  navigate,
  onBackToChat,
}: {
  intent: BuyIntent;
  balanceUsd: number;
  t: ReturnType<typeof useT>;
  navigate: ReturnType<typeof useNavigate>;
  onBackToChat: () => void;
}) {
  const required = intent.required;
  const ratio = required > 0 ? Math.min(1, balanceUsd / required) : 0;
  const missing = Math.max(0, required - balanceUsd);
  const ready = balanceUsd >= required;

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onContinue = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      const draft = await createOrderDraft({
        marketSlug: intent.slug,
        outcomeLabel: intent.outcome,
        side: 'BUY',
        amount: intent.usd,
      });
      navigate(`/sign-order?orderId=${encodeURIComponent(draft.orderId)}`);
    } catch (err) {
      setError((err as Error).message);
      setSubmitting(false);
    }
  }, [intent, navigate]);

  const body = t('deposit.intent_buy_body')
    .replace('{outcome}', escapeHtml(intent.outcome))
    .replace('{usd}', intent.usd.toFixed(2))
    .replace('{required}', required.toFixed(2));

  const progress = t('deposit.intent_progress')
    .replace('{balance}', balanceUsd.toFixed(2))
    .replace('{required}', required.toFixed(2));

  const short = t('deposit.intent_short').replace(
    '{missing}',
    missing.toFixed(2),
  );

  if (ready) {
    return (
      <Card className="border-emerald-500/40 bg-emerald-500/5">
        <CardEyebrow className="text-emerald-300">
          <CheckIcon weight="bold" className="size-3" />
          {t('deposit.intent_ready_title')}
        </CardEyebrow>
        <CardTitle className="text-base sm:text-lg">
          {t('deposit.intent_ready_title')}
        </CardTitle>
        <p className="text-muted-foreground text-[13px] leading-relaxed">
          {t('deposit.intent_ready_body')}
        </p>
        {error && <p className="text-destructive text-[12px]">{error}</p>}
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            size="lg"
            onClick={onContinue}
            disabled={submitting}
            className="flex-1"
          >
            {submitting && (
              <SpinnerIcon weight="bold" className="mr-2 size-4 animate-spin" />
            )}
            {submitting
              ? t('deposit.intent_continuing')
              : t('deposit.intent_continue_button')}
          </Button>
          <Button
            size="lg"
            variant="outline"
            onClick={onBackToChat}
            disabled={submitting}
          >
            {t('deposit.intent_back_to_chat')}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="border-primary/40 bg-primary/5">
      <CardEyebrow className="text-primary">
        <WarningIcon weight="fill" className="size-3" />
        {t('deposit.intent_buy_title')}
      </CardEyebrow>
      <CardTitle className="text-base sm:text-lg">
        {t('deposit.intent_buy_title')}
      </CardTitle>
      <p
        className="text-muted-foreground text-[13px] leading-relaxed"
        dangerouslySetInnerHTML={{ __html: body }}
      />
      <div className="flex flex-col gap-1.5">
        <div
          className="text-muted-foreground text-[12px]"
          dangerouslySetInnerHTML={{ __html: progress }}
        />
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-foreground/10">
          <div
            className="h-full bg-primary transition-[width] duration-500 ease-out"
            style={{ width: `${(ratio * 100).toFixed(1)}%` }}
          />
        </div>
        <div
          className="text-primary text-[12px] font-medium"
          dangerouslySetInnerHTML={{ __html: short }}
        />
      </div>
    </Card>
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
